import { CompressOutlined as CompactIcon } from "@mui/icons-material";
import { Box, Chip, Collapse, Typography } from "@mui/material";
import { useState } from "react";
import { MarkdownBlock } from "../markdown-block";

export interface ArgusCompactionDividerProps {
  /** Context tokens right before compaction ran. Null for old entries missing the field. */
  tokensBefore: number | null;
  /** LLM-generated summary that replaces the compacted history in future context windows. */
  summary: string;
}

/**
 * Inline divider chip rendered wherever the agent auto-compacted conversation
 * context. Lives in the transcript between the summarised (discarded) history
 * and the kept-recent tail so users can see when the agent "forgot" earlier
 * turns. Click to expand the summary text.
 *
 * Emitted by (a) the live-stream `compaction_end` event and (b) archived
 * JSONL compaction entries, translated into `role: "compaction"` pseudo-
 * messages by `be/api/argus/client.py::_parse_jsonl_to_messages`.
 */
export const ArgusCompactionDivider = ({ tokensBefore, summary }: ArgusCompactionDividerProps) => {
  const [expanded, setExpanded] = useState(false);
  const hasSummary = summary.trim().length > 0;

  const label =
    tokensBefore != null
      ? `Compacted · ${tokensBefore.toLocaleString()} tokens summarised`
      : "Compacted";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        my: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}>
        <Box sx={{ flex: 1, height: "1px", backgroundColor: "divider" }} />
        <Chip
          size="small"
          icon={<CompactIcon sx={{ fontSize: 14 }} />}
          label={label}
          variant="outlined"
          onClick={hasSummary ? () => setExpanded((v) => !v) : undefined}
          sx={{
            cursor: hasSummary ? "pointer" : "default",
            color: "text.secondary",
            borderColor: "divider",
            "& .MuiChip-icon": { color: "text.secondary" },
          }}
        />
        <Box sx={{ flex: 1, height: "1px", backgroundColor: "divider" }} />
      </Box>
      {hasSummary && (
        <Collapse in={expanded} sx={{ width: "100%" }}>
          <Box
            sx={{
              mt: 1,
              px: 2,
              py: 1.5,
              borderRadius: 1,
              backgroundColor: "action.hover",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
            >
              Compaction summary
            </Typography>
            <MarkdownBlock content={summary} />
          </Box>
        </Collapse>
      )}
    </Box>
  );
};
